import { formatDistanceToNow, format, isToday, isYesterday } from "date-fns";
import type { Expense } from "@shared/schema";

export type ExpenseWithDoc = Expense & { documentFileName?: string | null; accountNickname?: string | null; accountLast4?: string | null; accountIncludedInPnl?: boolean | null };

// Task #2131 — broaden Mercury detection so the Expenses tab treats bank
// imports ("mercury") and IO Credit Card imports ("mercury_card") the same
// for badges, filter, exclude/delete UI, and edit-form banner.
export function isMercurySource(s: string | null | undefined): boolean {
  return s === "mercury" || s === "mercury_card";
}

// Ledger safety actions apply to both providers. Keep Mercury detection
// separate so Stripe never enters Mercury-specific reconciliation workflows.
export function isImportedBankSource(s: string | null | undefined): boolean {
  return isMercurySource(s) || s === "stripe_bank";
}

export const EXPENSE_CATEGORIES = [
  { label: "Mortgage", value: "mortgage" },
  { label: "Property Tax", value: "property_tax" },
  { label: "Insurance", value: "insurance" },
  { label: "HOA", value: "hoa" },
  { label: "Utilities - Electric", value: "utilities_electric" },
  { label: "Utilities - Gas", value: "utilities_gas" },
  { label: "Utilities - Water", value: "utilities_water" },
  { label: "Utilities - Sewer", value: "utilities_sewer" },
  { label: "Utilities - Trash", value: "utilities_trash" },
  { label: "Utilities - Internet", value: "utilities_internet" },
  { label: "CapEx", value: "capex" },
  { label: "Repairs & Maintenance", value: "repairs_maintenance" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Lawn Care / Landscaping", value: "lawn_care" },
  { label: "Pest Control", value: "pest_control" },
  { label: "Property Management", value: "property_management" },
  { label: "PadSplit Management Fee", value: "padsplit_mgmt_fee" },
  { label: "Furnishings", value: "furnishings" },
  { label: "Supplies", value: "supplies" },
  { label: "Legal & Professional", value: "legal_professional" },
  { label: "Marketing", value: "marketing" },
  { label: "Travel", value: "travel" },
  { label: "Uncategorized", value: "other" },
];

export function getCategoryLabel(value: string, customCategories?: {label: string, value: string, isBalanceSheet?: boolean}[]): string {
  const built = EXPENSE_CATEGORIES.find((c) => c.value === value);
  if (built) return built.label;
  const custom = customCategories?.find((c) => c.value === value);
  if (custom) return custom.label;
  return value;
}

export function highlightMatch(text: string | null | undefined, query: string): React.ReactNode {
  if (!text) return text ?? null;
  const q = query.trim();
  if (!q) return text;
  const lowerText = text.toLowerCase();
  const lowerQ = q.toLowerCase();
  const parts: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    const idx = lowerText.indexOf(lowerQ, i);
    if (idx === -1) {
      parts.push(text.slice(i));
      break;
    }
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark
        key={key++}
        className="bg-yellow-200 dark:bg-yellow-700 text-inherit rounded-sm px-0.5"
      >
        {text.slice(idx, idx + lowerQ.length)}
      </mark>
    );
    i = idx + lowerQ.length;
  }
  return parts;
}

export function fmtCurrency(val: number): string {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}

// Task #1905 — Mercury accounts & balances dashboard panel.
// Task #2052: graduated out of the `mercury_balances` beta — visible to
// every Growth+ host with an active Mercury connection. The underlying
// /api/app/mercury/accounts route no longer requires the beta flag.
export type MercuryAccountRow = {
  id: number;
  mercuryAccountId: string;
  nickname: string | null;
  kind: string | null;
  accountNumberLast4: string | null;
  currentBalance: string | null;
  availableBalance: string | null;
  currency: string;
  lastSyncedAt: string | null;
  syncEnabled: boolean;
  // Task #2369 — when false, the account is hidden from the P&L tab and its
  // imported expenses/credits are excluded from all P&L/ledger queries.
  includedInPnl: boolean;
};
export type MercuryAccountsResponse = { connected: boolean; accounts: MercuryAccountRow[] };

// Task #4003 — per-card breakdown row for the expandable detail under the
// aggregate "Mercury Credit Cards" P&L line.
export type MercuryCreditAccountRow = {
  id: string;
  nickname: string | null;
  last4: string | null;
  outstandingBalance: number;
  creditLimit: number;
  availableCredit: number;
  currency: string;
  // Task #4039 — Mercury card IDs belonging to this credit account, used to
  // expand a breakdown row and load that card's recent transactions. Present
  // only for beta hosts; a row with no card IDs renders non-expandable.
  cardIds?: string[];
};

// Task #4039 — one Mercury card transaction, as returned by
// GET /api/app/mercury/cards/:cardId/transactions.
export type MercuryCardTxn = {
  id: string;
  amount: number;
  date: string;
  vendor: string | null;
  category: string | null;
  description: string | null;
};

// Task #3938 — live credit limit + outstanding balance aggregate for the
// Mercury IO Credit Card accounts, surfaced as a line in the P&L summary panel.
// Task #4003 — `accounts` carries the per-card breakdown shown under the line.
export type MercuryCreditSummaryResponse = {
  connected: boolean;
  totalCreditLimit: number;
  totalOutstandingBalance: number;
  totalAvailableCredit: number;
  accountCount: number;
  currency: string;
  accounts?: MercuryCreditAccountRow[];
};

// Task #4004 / #4041 — the Mercury credit-card "near its limit" threshold
// helpers now live in `shared/mercury-credit.ts` so the in-page P&L warning and
// the server-side push-style alert compute utilization identically. Re-exported
// here so existing client imports (ProfitAndLossTab, the unit test) keep working.
export {
  MERCURY_CREDIT_NEAR_LIMIT_UTILIZATION,
  mercuryCardUtilization,
  isMercuryCardNearLimit,
} from "@shared/mercury-credit";

export function formatBalance(value: string | null, currency: string): string {
  if (value === null) return "—";
  const n = parseFloat(value);
  if (isNaN(n)) return "—";
  try {
    return n.toLocaleString("en-US", { style: "currency", currency: currency || "USD" });
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

// Task #2127 — detect Mercury IO Credit Card accounts by their `kind` discriminator.
// Mercury returns kind="creditCard" or "ioCreditCard" for card accounts; we match
// case-insensitively on the substring "credit" to stay forward-compatible.
export function isCreditCard(kind: string | null): boolean {
  if (!kind) return false;
  return kind.toLowerCase().includes("credit");
}

export function formatCreditCardBalance(value: string | null, currency: string): string {
  if (value === null) return "—";
  const n = parseFloat(value);
  if (isNaN(n)) return "—";
  // Show as negative (amount owed) regardless of sign Mercury sends
  const abs = Math.abs(n);
  try {
    const formatted = abs.toLocaleString("en-US", { style: "currency", currency: currency || "USD" });
    return `−${formatted}`;
  } catch {
    return `−$${abs.toFixed(2)}`;
  }
}

export function formatLastSynced(value: string | null): string {
  if (!value) return "Never synced";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "Never synced";
  try {
    return `Last synced ${formatDistanceToNow(d, { addSuffix: true })}`;
  } catch {
    return "Never synced";
  }
}

// Task #2979 — section-level "Last synced" timestamp sourced from the most
// recent lastSyncedAt across all of the host's Mercury accounts. Renders an
// absolute time in the host's local (browser) timezone so hosts know when the
// daily auto-refresh (or a manual Refresh click) last ran.
export function mostRecentSyncedAt(accounts: { lastSyncedAt: string | null }[]): Date | null {
  let latest: Date | null = null;
  for (const a of accounts) {
    if (!a.lastSyncedAt) continue;
    const d = new Date(a.lastSyncedAt);
    if (isNaN(d.getTime())) continue;
    if (!latest || d.getTime() > latest.getTime()) latest = d;
  }
  return latest;
}

export function formatLastSyncedAbsolute(value: Date | null): string | null {
  if (!value) return null;
  try {
    const time = format(value, "h:mm a");
    if (isToday(value)) return `today at ${time}`;
    if (isYesterday(value)) return `yesterday at ${time}`;
    return format(value, "MMM d, yyyy 'at' h:mm a");
  } catch {
    return null;
  }
}

// Task #1904 — Mercury card-management panel (beta-gated). Rendered inside
// MercuryBankCard once the host has a working webhook connection. Calls 404
// silently when the beta flag is off, in which case the panel hides itself.
export type MercuryCardStatusInfo = {
  mercuryConnected: boolean;
  hasApiKey: boolean;
  linkedCardCount: number;
  propertyCount: number;
  propertiesMissingCard: { id: number; name: string }[];
  unassignedCards: { id: number; name: string | null; last4: string | null }[];
};

export const DEFAULT_HIGH_CONFIDENCE_SCORE = 85;
export const HIGH_CONFIDENCE_OPTIONS = [70, 80, 85, 90, 95] as const;
export const DEFAULT_AI_SUGGESTION_CONFIDENCE = 85;
export const AI_SUGGESTION_CONFIDENCE_OPTIONS = [60, 70, 80, 85, 90, 95] as const;

export interface IncomeRow {
  id: number;
  userId: number;
  propertyId: number | null;
  source: "mercury_credit" | "padsplit_earnings" | "manual" | "stripe_bank";
  category: string;
  amount: string;
  date: string;
  vendor: string | null;
  description: string | null;
  notes: string | null;
  externalId: string | null;
  externalSource: string | null;
  reconciliationStatus: string;
  createdAt: string;
  isCompanyLevel?: boolean | null;
  accountNickname?: string | null;
  accountLast4?: string | null;
  reconLinks: Array<{ id: number; depositIncomeId: number; earningsIncomeId: number; matchedAmount: string; variance: string; status: string }>;
  excludedAt?: string | null;
  // Revenue triage — human-confirmation stamp; null/absent = still "For review".
  reviewedAt?: string | null;
}

// Task #3788 — Format a Mercury account's display label for the Income/Expense
// list "Account" column. Always surfaces the last-4 for Mercury rows that have
// it (nickname-only would hide which account a transaction hit). Returns null
// when there is neither a nickname nor a last-4 (caller renders an em dash).
//   nickname + last4 → "Ops Checking · 7788"
//   last4 only       → "•••• 7788"
//   nickname only     → "Ops Checking"
export function formatMercuryAccountLabel(
  nickname: string | null | undefined,
  last4: string | null | undefined,
): string | null {
  if (last4) return nickname ? `${nickname} · ${last4}` : `•••• ${last4}`;
  return nickname ?? null;
}

// Task #3788 — Build the P&L "Other income" rows (Mercury credits + manual
// income grouped by category) from an explicit source list. Used for BOTH the
// property income rows and the company-level "Company Operations" income group,
// so the same math drives both and company income is never double-counted into
// the property rows. `keyPrefix` keeps the React keys unique across the two
// renders. A row is dropped when its YTD and every month are zero.
export type PnlIncomeGroupRow = {
  key: string;
  label: string;
  isPadSplit: boolean;
  ytd: number;
  months: number[];
};

export function buildPnlIncomeRows(
  list: Pick<IncomeRow, "source" | "category" | "amount" | "date">[],
  months: { key: string }[],
  allIncomeCatMap: Map<string, string>,
  keyPrefix = "",
): PnlIncomeGroupRow[] {
  const year = new Date().getFullYear();
  const bankRow: PnlIncomeGroupRow = {
    key: `${keyPrefix}stripe_bank`,
    label: "Connected bank income",
    isPadSplit: false,
    ytd: list
      .filter((r) => r.source === "stripe_bank" && r.date.startsWith(String(year)))
      .reduce((sum, r) => sum + parseFloat(r.amount || "0"), 0),
    months: months.map((m) =>
      list.filter((r) => r.source === "stripe_bank" && r.date.startsWith(m.key))
        .reduce((sum, r) => sum + parseFloat(r.amount || "0"), 0),
    ),
  };
  const mercuryRow: PnlIncomeGroupRow = {
    key: `${keyPrefix}mercury_credit`,
    label: "Mercury credits",
    isPadSplit: false,
    ytd: list
      .filter((r) => r.source === "mercury_credit" && r.date.startsWith(String(year)))
      .reduce((sum, r) => sum + parseFloat(r.amount || "0"), 0),
    months: months.map((m) =>
      list
        .filter((r) => r.source === "mercury_credit" && r.date.startsWith(m.key))
        .reduce((sum, r) => sum + parseFloat(r.amount || "0"), 0),
    ),
  };
  // Group manual income by category — each active category becomes its own row.
  const manualRows = list.filter((r) => r.source === "manual");
  const manualCategories = Array.from(new Set(manualRows.map((r) => r.category || "other_income")));
  const manualCategoryRows: PnlIncomeGroupRow[] = manualCategories.map((cat) => {
    const label = cat === "other_income" ? "Manual income" : (allIncomeCatMap.get(cat) ?? cat);
    return {
      key: `${keyPrefix}manual_${cat}`,
      label,
      isPadSplit: false,
      ytd: manualRows
        .filter((r) => (r.category || "other_income") === cat && r.date.startsWith(String(year)))
        .reduce((sum, r) => sum + parseFloat(r.amount || "0"), 0),
      months: months.map((m) =>
        manualRows
          .filter((r) => (r.category || "other_income") === cat && r.date.startsWith(m.key))
          .reduce((sum, r) => sum + parseFloat(r.amount || "0"), 0),
      ),
    };
  });
  return [
    ...(mercuryRow.ytd !== 0 || mercuryRow.months.some((v) => v !== 0) ? [mercuryRow] : []),
    ...(bankRow.ytd !== 0 || bankRow.months.some((v) => v !== 0) ? [bankRow] : []),
    ...manualCategoryRows.filter((r) => r.ytd !== 0 || r.months.some((v) => v !== 0)),
  ];
}

export const INCOME_CATEGORIES: { label: string; value: string }[] = [
  { label: "PadSplit Earnings", value: "padsplit_earnings" },
  { label: "Membership Dues", value: "membership_dues" },
  { label: "Late Fees", value: "late_fees" },
  { label: "Application Fees", value: "application_fees" },
  { label: "Security Deposit", value: "security_deposit" },
  { label: "Cleaning Deposit", value: "cleaning_deposit" },
  { label: "Rental Income", value: "rental_income" },
  { label: "Other Income", value: "other_income" },
];
